import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { getApiErrorStatus, getApiRateLimit } from "@/lib/api-error";
import { formatDurationLong } from "@/lib/format-amount";
import { useCallback } from "react";

/**
 * The warning a contact reveal refused for going too fast deserves: how many
 * reveals the user may make, in what window, and when to try again — so the
 * refusal reads as a pace to keep, not as a failure to click through.
 *
 * Shared by every reveal surface (the employee table's email and phone cells,
 * the chat's contact card) so a throttle reads the same everywhere.
 *
 * @returns `warnIfRateLimited(error)`: shows the warning and returns true for
 *   a 429, returns false and shows nothing otherwise.
 */
export const useRateLimitWarning = () => {
  const { toast } = useToast();
  const { t, currentLanguage } = useTranslation();

  const warnIfRateLimited = useCallback(
    (error: unknown): boolean => {
      if (getApiErrorStatus(error) !== 429) return false;

      const rateLimit = getApiRateLimit(error);
      toast({
        variant: "warning",
        title: t("components.revealRateLimit.title"),
        description: rateLimit
          ? t("components.revealRateLimit.description", {
              count: rateLimit.limit,
              window: formatDurationLong(
                rateLimit.windowSeconds,
                currentLanguage,
              ),
              retryAfter: formatDurationLong(
                rateLimit.retryAfterSeconds,
                currentLanguage,
              ),
            })
          : t("components.revealRateLimit.fallback"),
      });
      return true;
    },
    [currentLanguage, t, toast],
  );

  return { warnIfRateLimited };
};
